import {
  Avatar,
  Box,
  Button,
  chakra,
  ChakraProps,
  Divider,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react"
import { useIntl, navigate } from "gatsby-plugin-intl"
import { observer } from "mobx-react-lite"
import React, { useRef, useState, useEffect } from "react"
import HamburgerIcon from "../../icons/Hamburger"
import OrderIcon from "../../icons/Order"
import { PlaneIcon } from "../../icons/Plane"
import ProfileIcon from "../../icons/Profile"
import whiteIcon from "../../images/icon-no-circle-white.png"
import UserStore from "../../store/UserStore"
import logo from "../../images/icon_opaque.png"
import "intersection-observer"
import {
  AUTH_MESSAGES,
  COMMON_MESSAGES,
  LANDING_MESSAGES,
  PROFILE_MESSAGES,
} from "../../intl/messages"
import { BrLink, LinkOverlay } from "../Misc/LinkOverlay"
import { bmify } from "../../api"
import LayoutStore from "../../store/LayoutStore"
import { NotificationDropdown } from "../Misc/Notification"
import { Messages } from "../Misc/Message"
let mouseListener = null

const AuthDropDownMenu = observer((props: ChakraProps) => {
  const intl = useIntl()
  return (
    <Menu>
      <MenuButton
        bg="transparent!important"
        borderRadius="2xl"
        size="sm"
        minW="unset"
        className="tag-dropdown-nav-menu"
        as={Button}
      >
        <Flex alignItems="center">
          <HamburgerIcon mr={2} />{" "}
          <Avatar size="sm" src={bmify(UserStore.me.avatarpic)} />
        </Flex>
      </MenuButton>
      <MenuList zIndex={3}>
        <Box>
          <MenuItem pos="relative">
            <LinkOverlay to="/profile/?page=profile">
              {intl.formatMessage(COMMON_MESSAGES.profile)}
            </LinkOverlay>
          </MenuItem>
          <MenuItem pos="relative">
            <LinkOverlay to="/profile/?page=trips">
              {intl.formatMessage(PROFILE_MESSAGES.mytrips)}
            </LinkOverlay>
          </MenuItem>
          <MenuItem pos="relative">
            <LinkOverlay to="/profile/?page=orders">
              {intl.formatMessage(PROFILE_MESSAGES.myorders)}
            </LinkOverlay>
          </MenuItem>
          <Divider my={2} />
          <MenuItem pos="relative">
            <LinkOverlay to="/shop-cheaper">
              {intl.formatMessage(COMMON_MESSAGES.shopcheaper)}
            </LinkOverlay>
          </MenuItem>
          <MenuItem pos="relative">
            <LinkOverlay to="/travel-get-paid">
              {intl.formatMessage(LANDING_MESSAGES.travel_and_get_paid)}
            </LinkOverlay>
          </MenuItem>
          <MenuItem pos="relative">
            <LinkOverlay to="/faq">
              {intl.formatMessage(COMMON_MESSAGES.helpcenter)}
            </LinkOverlay>
          </MenuItem>
          <MenuItem
            onClick={() => {
              LayoutStore.languagePickerModalVisible = true
            }}
            pos="relative"
          >
            <Text>
              <Text as="small">
                {intl.formatMessage(COMMON_MESSAGES.language)}
              </Text>
              <br />
              <Text as="strong">
                {intl.formatMessage(COMMON_MESSAGES[intl.locale])}
              </Text>
            </Text>
          </MenuItem>
          <MenuItem
            onClick={() => {
              UserStore.logout()
              navigate("/login")
            }}
            pos="relative"
          >
            {intl.formatMessage(COMMON_MESSAGES.logout)}
          </MenuItem>
        </Box>
      </MenuList>
    </Menu>
  )
})

const DropDownMenu = chakra(({ className }) => {
  const intl = useIntl()
  return (
    <Menu>
      <MenuButton
        _hover={{ bg: "outline.light" }}
        color="text.medium"
        borderRadius="2xl"
        size="sm"
        minW="unset"
        className={className + " tag-dropdown-nav-menu"}
        as={Button}
      >
        <HamburgerIcon mr={2} /> <ProfileIcon />
      </MenuButton>
      <MenuList zIndex={3}>
        <Box>
          <MenuItem pos="relative">
            <LinkOverlay to="/signup">
              {intl.formatMessage(COMMON_MESSAGES.signup)}
            </LinkOverlay>
          </MenuItem>
          <MenuItem pos="relative">
            <LinkOverlay to="/login">
              {intl.formatMessage(AUTH_MESSAGES.login)}
            </LinkOverlay>
          </MenuItem>
          <Divider my={2} />
          <MenuItem pos="relative">
            <LinkOverlay to="/shop-cheaper">
              {intl.formatMessage(COMMON_MESSAGES.shopcheaper)}
            </LinkOverlay>
          </MenuItem>
          <MenuItem pos="relative">
            <LinkOverlay to="/travel-get-paid">
              {intl.formatMessage(LANDING_MESSAGES.travel_and_get_paid)}
            </LinkOverlay>
          </MenuItem>
          <MenuItem pos="relative">
            <LinkOverlay to="/faq">
              {intl.formatMessage(COMMON_MESSAGES.helpcenter)}
            </LinkOverlay>
          </MenuItem>
          <MenuItem
            onClick={() => {
              LayoutStore.languagePickerModalVisible = true
            }}
            pos="relative"
          >
            <Text>
              <Text as="small">
                {intl.formatMessage(COMMON_MESSAGES.language)}
              </Text>
              <br />
              <Text as="strong">English</Text>
            </Text>
          </MenuItem>
        </Box>
      </MenuList>
    </Menu>
  )
})

const AuthNavbar = chakra(({ className, children }) => {
  const [isSticky, setIsSticky] = useState(false) // When the navbar is sticky and highlighted
  const [expanded, setExpanded] = useState(false) // When the navbar is clicked and expanded for some reason 
  const intl = useIntl()
  const ref = useRef()
  useEffect(() => {
    const cachedRef = ref.current

    const observer = new IntersectionObserver(
      ([e]) => setIsSticky(e.intersectionRatio < 1),
      { threshold: [1] }
    )

    observer.observe(cachedRef)
    return function () {
      observer.unobserve(cachedRef)
    }
  }, [])

  return (
    <Box
      w="100%"
      h="55px"
      zIndex={2}
      sx={{ ".tag-link": { transition: ".1s ease" } }}
      ref={ref}
      aria-expanded={expanded}
      data-highlighted={isSticky || undefined}
      className={className}
      as="nav"
      px={[1, 2, 5]}
      alignItems="center"
      bg="white"
    >
      <Box mx="auto" pos="relative" h="100%" d="flex" alignItems="center">
        <BrLink
          d="inline-flex"
          className="tag-home-link"
          flex="1 0 50px"
          alignItems="center"
          h="7"
          to="/"
        >
          <Image
            d={["none", "none", "inline-flex"]}
            mr={[1, 2]}
            className="logo"
            h="100%"
            alt="Briddgy Logo"
            src={logo}
          />
          <Image
            d={["none", "none", "none"]}
            mr={[1, 2]}
            className="logo-white"
            h="100%"
            alt="Briddgy Logo"
            src={whiteIcon}
          />
          <Text fontSize="600" as="strong">
            Briddgy
          </Text>
        </BrLink>
        {children ? (
          children(expanded, setExpanded, isSticky)
        ) : (
          <Box d={["block", "block", "none"]}>
            <BrLink
              className="tag-link tag-shop-cheaper-link"
              _hover={{ bg: "success.light", color: "success.base" }}
              p={2}
              borderRadius="base"
              borderColor="outline.light"
              alignItems="center"
              mr={[1, 1, 5]}
              to="/shop-cheaper/"
            >
              <OrderIcon mt="-2px" mr={1} fontSize="500" color="inherit" />
              <Text
                textAlign="center"
                px={0}
                fontWeight="500"
                as="span"
                color="inherit"
              >
                {intl.formatMessage(COMMON_MESSAGES.shopcheaper)}
              </Text>
            </BrLink>
            <BrLink
              className="tag-link tag-travel-get-paid-link"
              _hover={{ bg: "cherryRed.light", color: "cherryRed.base" }}
              p={2}
              alignItems="center"
              borderRadius="base"
              borderColor="outline.light"
              mr={[1, 1, 5]}
              to="/travel-get-paid/"
            >
              <PlaneIcon mt="-2px" mr={1} fontSize="500" color="inherit" />
              <Text
                textAlign="center"
                px={0}
                fontWeight="500"
                as="span"
                color="inherit"
              >
                {intl.formatMessage(LANDING_MESSAGES.travel_and_get_paid)}
              </Text>
            </BrLink>
          </Box>
        )}
        <Flex
          justifyContent="flex-end"
          flex="1 0 50px"
          flexShrink={0}
          alignItems="center"
          alignSelf="stretch"
          ml="auto"
        >
          <BrLink
            className="tag-link tag-shop-cheaper-link"
            _hover={{ bg: "success.light", color: "success.base" }}
            p={2}
            borderRadius="base"
            borderColor="outline.light"
            d={["none", "none", "inline-flex"]}
            alignItems="center"
            mr={[1, 1, 3]}
            to="/shop-cheaper/"
          >
            <OrderIcon mt="-2px" mr={1} fontSize="500" color="inherit" />
            <Text px={0} fontWeight="500" as="span" color="inherit">
              {intl.formatMessage(COMMON_MESSAGES.shopcheaper)}
            </Text>
          </BrLink>
          <BrLink
            className="tag-link tag-travel-get-paid-link"
            _hover={{ bg: "cherryRed.light", color: "cherryRed.base" }}
            p={2}
            d={["none", "none", "inline-flex"]}
            alignItems="center"
            borderRadius="base"
            borderColor="outline.light"
            borderWidth={["1px", "1px", "0"]}
            mr={[1, 1, 3]}
            to="/travel-get-paid/"
          >
            <PlaneIcon mt="-2px" mr={1} fontSize="500" color="inherit" />
            <Text px={0} fontWeight="500" as="span" color="inherit">
              {intl.formatMessage(LANDING_MESSAGES.travel_and_get_paid)}
            </Text>
          </BrLink>
          <NotificationDropdown />
          <Messages d={["none", "none", "flex"]} />
          <AuthDropDownMenu />
        </Flex>
      </Box>
    </Box>
  )
})

const NonAuthNavbar = chakra(({ className, children }) => {
  const [isSticky, setIsSticky] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const intl = useIntl()
  // const [isHidden, setHidden] = useState(false)
  const ref = useRef()
  useEffect(() => {
    const cachedRef = ref.current
    const observer = new IntersectionObserver(
      ([e]) => setIsSticky(e.intersectionRatio < 1),
      { threshold: [1] }
    )

    observer.observe(cachedRef)
    return function () {
      observer.unobserve(cachedRef)
    }
  }, [])

  return (
    <Box
      w="100%"
      h="55px"
      zIndex={2}
      sx={{ ".tag-link": { transition: ".1s ease" } }}
      ref={ref}
      aria-expanded={expanded}
      data-highlighted={isSticky || undefined}
      className={className}
      as="nav"
      px={[2, 2, 5]}
      alignItems="center"
      bg="white"
    >
      <Box mx="auto" pos="relative" h="100%" d="flex" alignItems="center">
        <BrLink
          d="inline-flex"
          className="tag-home-link"
          flex="1 0 50px"
          alignItems="center"
          h="7"
          to="/"
        >
          <Image
            d={["none", "none", "inline-flex"]}
            mr={[1, 2]}
            className="logo"
            h="100%"
            alt="Briddgy Logo"
            src={logo}
          />
          <Image
            d={["none", "none", "none"]}
            mr={[1, 2]}
            className="logo-white"
            h="100%"
            alt="Briddgy Logo"
            src={whiteIcon}
          />
          <Text fontSize="600" as="strong">
            Briddgy
          </Text>
        </BrLink>
        {children ? (
          children(expanded, setExpanded, isSticky)
        ) : (
          <Box d={["block", "block", "none"]}>
            <BrLink
              className="tag-link tag-shop-cheaper-link"
              _hover={{ bg: "success.light", color: "success.base" }}
              p={2}
              borderRadius="base"
              borderColor="outline.light"
              alignItems="center"
              mr={[1, 1, 5]}
              to="/shop-cheaper/"
            >
              <OrderIcon mt="-2px" mr={1} fontSize="500" color="inherit" />
              <Text px={0} fontWeight="500" as="span" color="inherit">
                {intl.formatMessage(COMMON_MESSAGES.shopcheaper)}
              </Text>
            </BrLink>
            <BrLink
              className="tag-link tag-travel-get-paid-link"
              _hover={{ bg: "cherryRed.light", color: "cherryRed.base" }}
              p={2}
              alignItems="center"
              borderRadius="base"
              borderColor="outline.light"
              mr={[1, 1, 5]}
              to="/travel-get-paid/"
            >
              <PlaneIcon mt="-2px" mr={1} fontSize="500" color="inherit" />
              <Text px={0} fontWeight="500" as="span" color="inherit">
                {intl.formatMessage(LANDING_MESSAGES.travel_and_get_paid)}
              </Text>
            </BrLink>
          </Box>
        )}
        <Flex
          justifyContent="flex-end"
          flex="1 0 50px"
          flexShrink={0}
          alignItems="center"
          alignSelf="stretch"
          ml="auto"
        >
          <BrLink
            className="tag-link tag-shop-cheaper-link"
            _hover={{ bg: "success.light", color: "success.base" }}
            p={2}
            borderRadius="base"
            borderColor="outline.light"
            d={["none", "none", "inline-flex"]}
            alignItems="center"
            mr={[1, 1, 5]}
            to="/shop-cheaper/"
          >
            <OrderIcon mt="-2px" mr={1} fontSize="500" color="inherit" />
            <Text px={0} fontWeight="500" as="span" color="inherit">
              {intl.formatMessage(COMMON_MESSAGES.shopcheaper)}
            </Text>
          </BrLink>
          <BrLink
            className="tag-link tag-travel-get-paid-link"
            _hover={{ bg: "cherryRed.light", color: "cherryRed.base" }}
            p={2}
            d={["none", "none", "inline-flex"]}
            alignItems="center"
            borderRadius="base"
            borderColor="outline.light"
            borderWidth={["1px", "1px", "0"]}
            mr={[1, 1, 5]}
            to="/travel-get-paid/"
          >
            <PlaneIcon mt="-2px" mr={1} fontSize="500" color="inherit" />
            <Text
              d={["none", "none", "inline"]}
              px={0}
              fontWeight="500"
              as="span"
              color="inherit"
            >
              {intl.formatMessage(LANDING_MESSAGES.travel_and_get_paid)}
            </Text>
          </BrLink>
          <DropDownMenu />
        </Flex>
      </Box>
    </Box>
  )
})

const NavbarDefault = observer((props: ChakraProps) => {
  if (UserStore.isLoggedIn) {
    return <AuthNavbar {...props} />
  }
  return <NonAuthNavbar {...props} />
})

export default NavbarDefault
